import React from 'react';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';
import {
  WatercraftHelmet,
  WatercraftSpinner,
  WatercraftWordmark,
} from '../../features/watercraft';
import {
  SubscriptionStatusResponse,
  useCreateBillingPortalSessionMutation,
  useGetSubscriptionStatusQuery,
} from '../../features/watercraft/watercraftSlice';
import { notification } from '../../utils/messaging';
import { WATERCRAFT_ROUTE } from '../../app/appConstants';

function formatDate(
  date: string | Date | dayjs.Dayjs | null | undefined
): string {
  return date ? dayjs(date).format('D MMM YYYY') : '';
}

function statusDescription(subscription: SubscriptionStatusResponse): string {
  const { status, current_period_end, trial_end, cancel_at_period_end } =
    subscription;
  if (cancel_at_period_end) {
    return `Your subscription cancels on ${formatDate(current_period_end)}.`;
  }
  if (status === 'past_due') {
    return 'Your last payment failed. Update your payment details to keep your access.';
  }
  if (status === 'trialing') {
    return `Your free trial ends on ${formatDate(trial_end)}.`;
  }
  if (subscription.is_lifetime) {
    return 'You have lifetime access.';
  }
  if (current_period_end) {
    return `Active — renews on ${formatDate(current_period_end)}.`;
  }
  return 'Your subscription is active.';
}

export default function WatercraftManagePage(): React.JSX.Element {
  const { data: subscription, isFetching } = useGetSubscriptionStatusQuery();
  const [createBillingPortalSession, { isLoading: openingPortal }] =
    useCreateBillingPortalSessionMutation();

  const handleOpenPortal = () => {
    createBillingPortalSession({ return_url: window.location.href })
      .unwrap()
      .then((redirectUrl) => {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      })
      .catch((err) => {
        notification.error({
          description:
            err?.status === 400
              ? 'No billing details found for your account.'
              : 'Unable to open the billing portal. Please try again.',
        });
      });
  };

  if (isFetching) {
    return (
      <div className='grid min-h-screen w-full place-content-center bg-watercraft-bg text-watercraft-text-primary'>
        <WatercraftHelmet />
        <WatercraftSpinner />
      </div>
    );
  }

  if (!subscription?.has_access) {
    return <Navigate to={WATERCRAFT_ROUTE} replace />;
  }

  return (
    <main className='flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-gradient-to-b from-watercraft-bg to-watercraft-surface p-6 text-watercraft-text-primary'>
      <WatercraftHelmet />
      <WatercraftWordmark className='h-9 w-auto text-watercraft-text-primary' />
      <section className='w-full max-w-sm rounded-xl border border-watercraft-border bg-watercraft-surface p-6'>
        <h1 className='text-2xl font-bold'>Your subscription</h1>
        <p className='mt-2 text-sm text-watercraft-text-secondary'>
          {statusDescription(subscription)}
        </p>

        {subscription.source === 'stripe' && (
          <Button
            type='primary'
            className='mt-6 h-11 w-full rounded-lg border-none bg-watercraft-primary text-base font-semibold text-watercraft-text-primary hover:bg-watercraft-primary-light'
            disabled={openingPortal}
            onClick={handleOpenPortal}
          >
            {openingPortal ? (
              <WatercraftSpinner className='h-5 w-5 border-watercraft-text-primary/30 border-t-watercraft-text-primary' />
            ) : (
              'Manage subscription'
            )}
          </Button>
        )}
        {(subscription.source === 'apple' ||
          subscription.source === 'google') && (
          <p className='mt-6 text-sm text-watercraft-text-secondary'>
            You&apos;re subscribed via the{' '}
            {subscription.source === 'apple' ? 'App Store' : 'Google Play'}.
            Manage your subscription there.
          </p>
        )}
        {(subscription.source === 'granted' ||
          subscription.source === 'complimentary') && (
          <p className='mt-6 text-sm text-watercraft-text-secondary'>
            You have complimentary watercraft access. Contact support for any
            changes.
          </p>
        )}
      </section>
    </main>
  );
}
